/**
 * Game page `/g/:slug` — the game's LANDING PAGE: a cover-art hero with a Start
 * button (the game loads only on Start, not on arrival), the description + all
 * the stats we keep, credits, a per-game comment thread, and (for creations) the
 * version history.
 *
 * ONE page body for every species (PR-H decision 1): native games are REAL
 * `creations` docs (`kind: 'native'`, seeded by tools/ops/seed-native-games.mjs)
 * so likes, view/play counters, credits, comments, and subscriptions run the
 * SAME code path as creations. The differences are data-driven off `kind` +
 * the native manifest. Start is a real LINK that opens the game STANDALONE in
 * the CURRENT tab (never an embedded iframe) so it runs full-window and can be
 * added to the home screen — cmd/ctrl-click opens a new tab like any link:
 *  - hosted natives open their manifest path (`/games/<slug>/`, installable PWA);
 *  - creations open the same-origin `/play/<id>/<version>/` proxy (installable);
 *  - EXTERNAL natives (IceSpace) open their live deployment (Start = "Open ↗"),
 *    but still carry the full doc-backed landing (stats + comments);
 *  - natives have no versions, so the version-history panel is hidden.
 * A native whose doc is not seeded yet degrades to a manifest-only landing
 * (playable/launchable, no social panels) instead of "Game not found".
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { Creation, CreationFilmMaterialV1, CreationVersion } from '@djgg/creations';
import { isComposedPipeline, supportsOnlineInvites } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { useSetActiveVersion } from '../hooks/useSetActiveVersion.js';
import { canCreate } from '../auth/model.js';
import { nativeGameBySlug, type NativeGame } from '../gallery/nativeGames.js';
import { creationToItem, nativeToItem, type GalleryItem } from '../gallery/model.js';
import { LikeButton } from '../components/LikeButton.js';
import { GameMediaStory, promotionalHighlights } from '../components/GameMediaStory.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { Comments } from '../components/Comments.js';
import { CreationLeaderboard } from '../components/CreationLeaderboard.js';
import { RecentPlayers } from '../components/RecentPlayers.js';
import { Fans } from '../components/Fans.js';
import { OnlineMatchPanel } from '../components/OnlineMatchPanel.js';
import { PreviewSoundToggle, publishedMusicUrl } from '../components/previewMusic.js';
import { Mascot } from '../components/Mascot.js';
import { CreationFilm } from '../studio/CreationFilm.js';
import type { ShowArtifact } from '../studio/creationShowModel.js';
import { playUrl } from '../services/playUrl.js';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard.js';
import { publicPortalOrigin } from '@djgg/cloud';

/** The shared "← Gallery" top row. */
function BackToGallery() {
  return (
    <div className="row" style={{ margin: '20px 0' }}>
      <Link to="/gallery" className="btn btn--ghost btn--sm">
        ← Gallery
      </Link>
    </div>
  );
}

/** Build wall-time from a version's createdAt→finishedAt, human-formatted.
 *  '' when either timestamp is missing (a build still in flight, or a legacy
 *  row that predates finishedAt). */
function buildElapsed(v: { createdAt?: number; finishedAt?: number | null }): string {
  if (!v.createdAt || !v.finishedAt) return '';
  const ms = v.finishedAt - v.createdAt;
  if (ms <= 0) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** Truncate a prompt/diff for the history table. */
function truncate(s: string, max = 140): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

const PUBLIC_DEEJ_FILM_IDS = new Set([
  'deej-film-spark-catcher', 'deej-film-rule-cartographer', 'deej-film-raw-art-receiver',
  'deej-film-cutout-technician', 'deej-film-casting-director', 'deej-film-beat-builder',
  'deej-film-crash-test-pilot', 'deej-film-fix-mechanic', 'deej-film-trailer-director',
]);

/** Plays only while a viewer can see it: the film sits below Play, and nobody
 *  should come back to find it already over. */
function PublishedCreationFilm({ creationId, title, manifest, materials }: {
  creationId: string;
  title: string;
  manifest: NonNullable<CreationVersion['creationFilmManifest']>;
  materials: readonly CreationFilmMaterialV1[];
}) {
  const panel = useRef<HTMLElement>(null);
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting ?? false),
      { threshold: 0.5 },
    );
    observer.observe(panel.current!);
    return () => observer.disconnect();
  }, []);
  return (
    <section ref={panel} className="panel creation-film-panel" aria-label={`${title} creation film`}>
      <p className="muted">One-of-a-kind creation film · version {manifest.version}</p>
      <CreationFilm
        identity={`${creationId}:published-film:v${manifest.version}`}
        title={title}
        activity="How this game came together"
        artifacts={publicFilmArtifacts(materials)}
        runSeed={manifest.runId}
        sealedManifest={manifest}
        active={onScreen}
      />
    </section>
  );
}

function publicFilmArtifacts(materials: readonly CreationFilmMaterialV1[]): ShowArtifact[] {
  return materials.map(material => {
    const deejUrl = material.kind === 'deej' && material.deejAssetId && PUBLIC_DEEJ_FILM_IDS.has(material.deejAssetId)
      ? `/mascot/${material.deejAssetId}.webp` : undefined;
    const url = material.kind === 'deej' ? deejUrl : material.uri;
    const kind: ShowArtifact['kind'] = material.kind === 'soundtrack' ? 'audio'
      : material.contentType?.startsWith('video/') ? 'video'
        : url ? 'image' : 'discovery';
    return {
      id: material.materialId,
      title: material.title,
      summary: material.description ?? material.text ?? '',
      kind,
      url,
      strip: material.strip,
      unavailable: !!material.unavailableReason || (material.kind === 'deej' && !deejUrl),
      category: material.kind === 'title' || material.kind === 'cover' ? 'identity'
        : material.kind === 'soundtrack' ? 'sound'
          : material.kind === 'gameplay-capture' || material.kind === 'playable-proof' ? 'proof'
            : material.kind === 'cue' ? 'gameplay' : 'extras',
      origin: 'existing',
      state: material.unavailableReason ? 'unavailable' : 'current',
    };
  });
}

/**
 * The lazy forks list (origin R8): the DIVERGED public forks of this game.
 * Rendered only when `forksCount > 0`, and the list is fetched on the FIRST
 * open — a viral page pays zero fork reads until someone asks. CONTROLLED:
 * the stats-row forks counter opens it too (and scrolls it into view).
 */
function ForksPanel({
  creationId,
  forksCount,
  open,
  onToggle,
  panelRef,
}: {
  creationId: string;
  forksCount: number;
  open: boolean;
  onToggle: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
}) {
  const services = useServices();
  const [forks, setForks] = useState<Array<Creation & { id: string }> | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open || forks !== null) return;
    setErr(''); // a retry must not render under a stale failure banner
    services.repos.creations
      .listForks(creationId)
      .then(setForks)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Failed to load forks.'));
  }, [open, forks, services, creationId]);
  if (forksCount === 0) return null;

  return (
    <div className="panel" data-testid="forks-panel" ref={panelRef}>
      <button className="btn btn--ghost btn--sm" onClick={onToggle} data-testid="forks-toggle">
        {open ? '▾' : '▸'} ⑂ {forksCount} fork{forksCount === 1 ? '' : 's'}
      </button>
      {open && (
        <div className="stack" style={{ marginTop: 10 }}>
          {err && <div className="error-text">{err}</div>}
          {!err && forks === null && <p className="muted">Loading…</p>}
          {forks?.map((f) => (
            <div className="row" key={f.id} data-testid="fork-row">
              <Link to={`/g/${f.slug}`}>{f.title}</Link>
              <span className="muted">
                by @{f.creatorHandle}
                {f.divergedAt ? ` — diverged ${new Date(f.divergedAt).toLocaleDateString()}` : ''}
              </span>
            </div>
          ))}
          {forks !== null && forks.length === 0 && (
            <>
              <Mascot context="forks-empty" size={240} quip={false} />
              <p className="muted">No public forks yet.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The cover-art HERO with its play affordances immediately below it. Start is a
 * real link to the standalone game URL (same-tab; cmd/ctrl-click for a new tab
 * like any link); external natives label it "Open ↗"; a not-yet-playable game
 * shows why below the artwork.
 *
 * Cover art is the primary Start affordance whenever the game is playable; the
 * explicit Start and Install controls remain below it. The anchors use the
 * same URL and start handler, so both paths launch/count identically.
 */
function GameHero({
  item,
  canPlay,
  isExternal,
  buildInProgress,
  status,
  startHref,
  installHref,
  musicUrl,
  onStart,
}: {
  item: GalleryItem;
  canPlay: boolean;
  isExternal: boolean;
  buildInProgress: boolean;
  status: string;
  startHref: string;
  installHref?: string;
  /** The active version's published music; null = none (no toggle). */
  musicUrl: string | null;
  onStart: () => void;
}) {
  return (
    <>
      <GameMediaStory
        slug={item.slug}
        title={item.title}
        coverUrl={item.coverUrl}
        posterUrl={item.posterUrl}
        titleOverlayUrl={item.titleOverlayUrl}
        previewUrl={canPlay ? item.previewUrl : undefined}
        previewKind={item.previewKind}
        previewProbeUrl={item.kind === 'native' ? item.previewProbeUrl : undefined}
        assetUrls={item.storyAssetUrls}
        highlights={promotionalHighlights(item)}
        active
        variant="hero"
        playHref={canPlay ? startHref : undefined}
        playLabel={isExternal ? `Open ${item.title}` : `Start ${item.title}`}
        onPlay={canPlay ? onStart : undefined}
        testId="game-hero"
        musicUrl={musicUrl}
        overlay={musicUrl ? <PreviewSoundToggle url={musicUrl} className="game-media-story__sound" /> : undefined}
      />
      {canPlay ? (
        <div className="game-hero__actions" data-testid="game-hero-actions">
          <a
            className="btn btn--primary game-hero__start"
            href={startHref}
            onClick={onStart}
            data-testid="start-game"
          >
            {isExternal ? `Open ${item.title} ↗` : '▶ Start'}
          </a>
          {installHref && (
            <a
              className="btn btn--ghost game-hero__install"
              href={installHref}
              onClick={onStart}
              data-testid="install-game"
            >
              ⇩ Install game
            </a>
          )}
        </div>
      ) : buildInProgress ? (
        <div className="game-hero__status pill" data-testid="first-build-indicator">
          Building the first playable version… check back in a minute.
        </div>
      ) : (
        <div className="game-hero__status pill" data-testid="not-playable">
          Not playable yet (status: {status}).
        </div>
      )}
    </>
  );
}

function CreationGamePage({ slug, native }: { slug: string; native?: NativeGame }) {
  const services = useServices();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { runAttributed, attributionModal } = useAttributedAction();
  const [creationId, setCreationId] = useState<string | null | undefined>(undefined);
  const [versionBusy, setVersionBusy] = useState<number | null>(null);
  const [versionErr, setVersionErr] = useState('');
  const [forking, setForking] = useState(false);
  const [forkErr, setForkErr] = useState('');
  const [forksOpen, setForksOpen] = useState(false);
  const forksPanelRef = useRef<HTMLDivElement | null>(null);
  const { copied: linkCopied, copy: copyLink } = useCopyToClipboard();

  // Hosted natives play from the MANIFEST path (first-party bundle); external
  // natives launch their live URL; both come from the manifest, not the doc.
  const hostedPath = native?.link.kind === 'hosted' ? native.link.path : undefined;
  const launchUrl = native?.link.kind === 'external' ? native.link.url : undefined;
  const isExternal = native?.link.kind === 'external';

  // View + engaged-play counting is tied to STARTING the game (Start), not
  // arriving on the landing, so the numbers track real plays. Both are recorded
  // ONCE, on open: launching navigates away from the landing, so the open IS
  // the engaged play (the model external natives already used). All best-effort
  // — fired on click, before the navigation; a missed count must never surface
  // as a pageerror.
  const viewed = useRef(false);
  const played = useRef(false);

  // Resolve slug → id once (claim-doc lookup via repo). Reset the view/play
  // guards too: React reuses this component instance across a game→game
  // navigation (same route), so without this the second game's counters would
  // stay suppressed by the first game's already-recorded flags.
  useEffect(() => {
    let active = true;
    viewed.current = false;
    played.current = false;
    services.repos.creations
      .resolveSlug(slug)
      .then((id) => {
        if (active) setCreationId(id);
      })
      .catch(() => {
        if (active) setCreationId(null);
      });
    return () => {
      active = false;
    };
  }, [services, slug]);

  const creation = useSubscription<(Creation & { id: string }) | null>(
    (cb) => (creationId ? services.subscribe.creation(creationId, cb) : () => {}),
    null,
    [creationId],
  );
  const versions = useSubscription<Array<CreationVersion & { id: string }>>(
    (cb) => (creationId ? services.subscribe.versions(creationId, cb) : () => {}),
    [],
    [creationId],
  );
  const filmVersionNumber = creation ? creation.activeVersion || creation.latestVersion : 0;
  const publicFilmManifest = versions.find(version => version.n === filmVersionNumber)?.creationFilmManifest ?? null;
  const publicFilmMaterials = useSubscription<CreationFilmMaterialV1[]>(
    (cb) => {
      if (!publicFilmManifest) {
        cb([]);
        return () => {};
      }
      return services.subscribe.creationFilmMaterials(publicFilmManifest.runId, cb);
    },
    [],
    [publicFilmManifest?.runId],
  );
  const liked = useSubscription<boolean>(
    (cb) => (creationId && user ? services.subscribe.isLiked(creationId, user.uid, cb) : () => {}),
    false,
    [creationId, user?.uid],
  );
  // Called ahead of the loading/not-found early returns below (rules of
  // hooks) — the hook itself is a no-op while `creation` is still null.
  const {
    setActive,
    activatingVersion,
    error: activateErr,
  } = useSetActiveVersion(creation, runAttributed);

  if (creationId === undefined) return <div className="empty">Loading…</div>;
  if (creationId === null || creation === null) {
    // A native whose doc isn't seeded (fresh env, emulator) stays PLAYABLE from
    // the manifest — just without the doc-backed social panels.
    if (native) {
      return (
        <div className="container">
          <BackToGallery />
          <h1 className="gallery__title">{native.title}</h1>
          <p className="gallery__subtitle">{native.description}</p>
          <div style={{ margin: '18px 0' }}>
            {hostedPath ? (
              <div className="row">
                <a className="btn btn--primary" data-testid="start-game" href={hostedPath}>
                  ▶ Start
                </a>
                <a
                  className="btn btn--ghost game-hero__install"
                  data-testid="install-game"
                  href={`${hostedPath}?install=1`}
                >
                  ⇩ Install game
                </a>
              </div>
            ) : (
              <a className="btn btn--primary" href={launchUrl}>
                Open {native.title} ↗
              </a>
            )}
          </div>
          <span className="pill">native djgg game</span>
        </div>
      );
    }
    return (
      <div className="empty">
        <Mascot context="not-found" />
        Game not found.
      </div>
    );
  }

  const isNative = creation.kind === 'native';
  // The published-bundle URL: a hosted native's manifest path, or a creation's
  // ACTIVE bundle (falling back to latest). Used to gate online-play eligibility;
  // the actual launch goes through `standaloneUrl` (native path or /play proxy).
  const bundleUrl = isNative ? hostedPath : creation.activeBundleUrl || creation.latestBundleUrl;
  const activeVersion = creation.activeVersion || creation.latestVersion;
  // Playability is DECOUPLED from build progress: a live game iterating a new
  // version stays playable. External natives are playable when we have a launch
  // url; hosted natives when the manifest path exists; a creation when it has an
  // active version with a published bundle.
  const canPlay = isExternal
    ? !!launchUrl
    : isNative
      ? !!bundleUrl
      : (creation.activeVersion ?? 0) > 0 && !!creation.activeBundleUrl;
  const buildInProgress = creation.buildState === 'building';
  const mediaItem = isNative && native ? nativeToItem(native, creation) : creationToItem(creation);
  // The latest version doc drives the history note (rolled-back vs failed-latest).
  const latestVersionDoc = versions.find((v) => v.n === creation.latestVersion) ?? null;
  // ONLINE play mode (A4.4, ranked): the eligibility read is the v4 play-mode
  // SET — `settings.playModes` includes 'onlineVersus' (the old scalar
  // `playerMode` is dead). `hasOnlineMode` alone gates the ranked-ladder panel
  // (the ladder persists even while a rebuilt version lacks a sim bundle);
  // `onlineEligible` additionally needs the ACTIVE version to have published a
  // headless sim the platform can host. `inviteReady` further requires that
  // version's baked shell to honor the `?match=` pairing key (>=0.8.0) — an
  // un-repackaged bundle would pair non-deterministically, so the Arena panel
  // shows a "needs repackage" note instead.
  const activeVersionDoc = versions.find((v) => v.n === activeVersion) ?? null;
  // The RUNTIME game key — how every play-side surface addresses this game:
  // a creation by its id, a NATIVE by its slug (`games/tetris/leaderboard/...`,
  // the gameId its descriptor declares; the creations doc for a native is
  // `native-<slug>`, which no snapshot is ever written under). The same
  // vocabulary the presence beat and the play history use, so the board and the
  // recent-players rail key off one value rather than two lookalikes.
  const gameKey = isNative ? slug : creationId;
  const hasOnlineMode =
    !isNative && (creation.settings?.playModes ?? []).includes('onlineVersus');
  const onlineEligible = hasOnlineMode && !!activeVersionDoc?.simPath && !!bundleUrl;
  const inviteReady = onlineEligible && supportsOnlineInvites(activeVersionDoc?.shellVersion);

  // The standalone URL Start opens per kind: external → its live URL; hosted
  // native → the first-party bundle path (`/games/<slug>/`); creation → the
  // same-origin `/play/<id>/` proxy (so it's installable as a web app).
  //
  // FLOATING, not pinned to `activeVersion`. Start is also what a player
  // installs from, and an installed app relaunches its start URL for ever — a
  // pinned one froze players on their install-time version. Online play is NOT
  // launched from here; it goes through the Arena panel (host/join), which
  // pins, so two players pair on one identical build.
  const standaloneUrl = (): string => {
    if (isExternal && launchUrl) return launchUrl;
    if (isNative && hostedPath) return hostedPath;
    return playUrl(creationId);
  };

  // Start/Play-online are REAL LINKS — the browser owns the navigation (current
  // tab on a plain click, new tab on cmd/ctrl-click like any other link). This
  // click handler only records the view + engaged play; it never prevents the
  // default navigation.
  const recordStart = (): void => {
    if (!viewed.current) {
      viewed.current = true;
      services.recordView(creationId).catch(() => {
        /* view counting is best-effort */
      });
    }
    if (!played.current) {
      played.current = true;
      services.recordPlay(creationId).catch(() => {
        /* play counting is best-effort */
      });
    }
  };

  // A ROLLBACK is a deliberate pin of an OLDER version as active while a NEWER
  // LIVE version exists. A merely-FAILED latest iteration is NOT a rollback —
  // the active version simply stayed put — so the copy must not conflate them.
  const rolledBack =
    activeVersion < creation.latestVersion && latestVersionDoc?.buildStatus === 'live';
  const latestFailed =
    activeVersion !== creation.latestVersion && latestVersionDoc?.buildStatus === 'failed';
  const editors = creation.editors ?? [];
  // Manage (build/set-active/update) = any MEMBER (creator or editor) or admin;
  // destroy (delete version / creation) stays creator-or-admin only.
  const canManage =
    !!user &&
    (user.uid === creation.creatorUid ||
      (creation.memberUids ?? []).includes(user.uid) ||
      user.role === 'superadmin');
  const canDestroy = !!user && (user.uid === creation.creatorUid || user.role === 'superadmin');
  const publishedOn = creation.createdAt ? new Date(creation.createdAt).toLocaleDateString() : '';
  // MEMBERSHIP proper (no admin shortcut): the not-published hint below is a
  // member-only nudge to the Studio publish control — non-members (admins
  // included) see nothing new on an unlisted page they reached by link.
  const isMember =
    !!user &&
    (user.uid === creation.creatorUid || (creation.memberUids ?? []).includes(user.uid));
  const canEdit = !!user && !user.isAnonymous && isMember;

  // Fork (origin R1): any signed-in platform account, on a public non-native game with
  // a live version (canPlay is exactly that test for creations) whose ACTIVE
  // version has a source archive to seed from — games built before source
  // archiving shipped have none and can never fork until rebuilt (live
  // finding: the button showed on one and the server rightly refused). The
  // server re-enforces all of it; the button is just honest about eligibility.
  const canFork =
    canCreate(user) &&
    !isNative &&
    creation.visibility === 'public' &&
    canPlay &&
    !!activeVersionDoc?.srcPath && isComposedPipeline(activeVersionDoc.pipelineSnapshot);
  const handleFork = (): void => {
    setForkErr('');
    setForking(true);
    services.callable
      .forkCreation({ creationId, initialIntent: '', commandId: crypto.randomUUID() })
      .then((r) => navigate(`/studio?game=${r.creationId}`))
      .catch((e: unknown) => {
        setForkErr(e instanceof Error ? e.message : 'Fork failed.');
        setForking(false);
      });
  };

  // Delete rides the attribution wrapper directly (setActive's is inside
  // useSetActiveVersion): a superadmin who is not a member picks the
  // collaborator the change is credited to first.
  const removeVersion = (n: number): void => {
    if (!window.confirm(`Delete version v${n}? This cannot be undone.`)) return;
    void runAttributed(creation, async (attributeTo) => {
      setVersionErr('');
      setVersionBusy(n);
      try {
        await services.callable.deleteVersion({ creationId, version: n, attributeTo });
      } catch (e) {
        setVersionErr(e instanceof Error ? e.message : 'Failed to delete version.');
      } finally {
        setVersionBusy(null);
      }
    });
  };

  return (
    <div className="container">
      <BackToGallery />
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="gallery__title">{creation.title}</h1>
          <p className="gallery__subtitle">{creation.description}</p>
          {creation.forkedFrom && (
            // Credit-via-lineage (origin R3/R8): rendered from the fork-time
            // snapshot — no lookups, and it survives parent deletion/renames.
            <p className="muted" data-testid="fork-lineage" style={{ fontSize: 13, marginTop: 4 }}>
              ⑂ Forked from <Link to={`/g/${creation.forkedFrom.slug}`}>{creation.forkedFrom.title}</Link>
              {' — originally by @'}
              {creation.rootCreatorHandle || creation.forkedFrom.creator.handle}
              {creation.forkedFrom.editors.length > 0 &&
                `, with ${creation.forkedFrom.editors.map((e) => `@${e.handle || e.uid}`).join(', ')}`}
            </p>
          )}
          {isNative && <span className="pill">native djgg game</span>}
          {creation.editorsPick?.everSelected && (
            <div
              className={`editors-pick-banner${
                creation.editorsPick.current ? ' editors-pick-banner--current' : ''
              }`}
              data-testid="editors-pick-banner"
            >
              <img src="/editors-pick/laurel.webp" alt="" aria-hidden="true" />
              <div className="editors-pick-banner__text">
                <strong>
                  {creation.editorsPick.current ? "Current Editors' Pick" : "Editors' Pick"}
                </strong>
                <span>
                  Official Selection
                  {creation.editorsPick.editions?.length
                    ? ` · ${creation.editorsPick.editions[creation.editorsPick.editions.length - 1]}`
                    : ''}
                  {creation.editorsPick.selectionCount > 1
                    ? ` · ${creation.editorsPick.selectionCount}× selected`
                    : ''}
                  {(creation.editorsPick.reviewedVersion ?? 0) > 0
                    ? ` · reviewed at v${creation.editorsPick.reviewedVersion}`
                    : ''}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          {canEdit && (
            <Link to={`/studio/g/${creationId}`} className="btn btn--ghost btn--sm" data-testid="edit-game">
              Edit
            </Link>
          )}
          {canFork && (
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleFork}
              disabled={forking}
              data-testid="fork-game"
              title="Fork this game into your studio as a private copy"
            >
              {forking ? 'Copying…' : '⑂ Copy game'}
            </button>
          )}
          <LikeButton
            creationId={creationId}
            initialLiked={liked}
            initialCount={creation.likesCount}
            serverLiked={liked}
            serverCount={creation.likesCount}
          />
        </div>
      </div>
      {forkErr && <div className="error-text">{forkErr}</div>}

      <div style={{ margin: '18px 0' }}>
        <GameHero
          item={mediaItem}
          canPlay={canPlay}
          isExternal={isExternal}
          buildInProgress={buildInProgress}
          status={creation.status}
          startHref={standaloneUrl()}
          installHref={!isExternal ? `${standaloneUrl()}?install=1` : undefined}
          musicUrl={publishedMusicUrl(creation, activeVersionDoc)}
          onStart={recordStart}
        />
        {canPlay && !isExternal && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>
              Opens full-screen — add it to your home screen to play like an app.
            </p>
            {/* The FLOATING url, absolute so it survives being pasted anywhere.
                A link a player keeps outlives the version that was live when
                they saved it, so what gets shared names the game, not a build. */}
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              data-testid="copy-play-link"
              style={{ marginTop: 6 }}
              onClick={() => void copyLink(`${publicPortalOrigin(window.location)}${playUrl(creationId)}`)}
            >
              {linkCopied ? '✓ Link copied' : 'Copy link'}
            </button>
          </div>
        )}
        {buildInProgress && !isNative && (
          <div className="pill" data-testid="building-indicator" style={{ marginTop: 8 }}>
            A new version is building — you’re playing the current one.
          </div>
        )}
        {/* Member-only publish-gate hint: an unlisted game looks identical to a
            published one from here, so tell the people who can fix that where
            the control lives. Copy stays honest — unlisted is link-playable,
            never "hidden"; a draft fork genuinely IS member-only until it
            diverges (RLS), and no one can flip it early. */}
        {!isNative && isMember && creation.visibility !== 'public' && (
          <div className="pill" data-testid="unpublished-hint" style={{ marginTop: 8 }}>
            {creation.visibility === 'draft' ? (
              <>
                Private fork — visible only to its members until a change ships.{' '}
                <Link to={`/studio/g/${creationId}?tab=overview`}>Iterate in your Studio</Link> to
                unlock publishing.
              </>
            ) : (
              <>
                Not published — this game isn’t listed in the gallery, but anyone with the link can
                play. <Link to={`/studio/g/${creationId}?tab=overview`}>Publish it from your Studio</Link>.
              </>
            )}
          </div>
        )}
      </div>

      {publicFilmManifest && publicFilmMaterials.length > 0 && (
        <PublishedCreationFilm creationId={creationId} title={creation.title} manifest={publicFilmManifest} materials={publicFilmMaterials} />
      )}

      <div className="panel">
        <h2 className="panel__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          By <UserAvatar uid={creation.creatorUid} size={20} />
          <Link to={`/u/${creation.creatorHandle}`}>@{creation.creatorHandle}</Link>
        </h2>
        <div className="row muted game-stats" data-testid="game-stats">
          <span className="style-badge">{creation.style}</span>
          <span>{creation.playsCount} plays</span>
          <span>{creation.viewsCount} views</span>
          <span>{creation.likesCount} likes</span>
          <span>{creation.commentsCount ?? 0} comments</span>
          {!isNative &&
            ((creation.forksCount ?? 0) > 0 ? (
              // Clickable: opens the forks panel below and scrolls to it.
              <button
                type="button"
                data-testid="forks-count"
                title="See the forks of this game"
                onClick={() => {
                  setForksOpen(true);
                  setTimeout(
                    () => forksPanelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
                    50,
                  );
                }}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {creation.forksCount} forks
              </button>
            ) : (
              <span data-testid="forks-count">0 forks</span>
            ))}
          {!isNative && creation.latestVersion > 0 && <span>v{creation.latestVersion}</span>}
          {publishedOn && <span>since {publishedOn}</span>}
        </div>
      </div>

      <div className="panel" data-testid="credits">
        <h2 className="panel__title">Credits</h2>
        <div className="stack">
          <div className="row" data-testid="credit-row">
            <UserAvatar uid={creation.creatorUid} size={20} />
            <Link to={`/u/${creation.creatorHandle}`}>@{creation.creatorHandle}</Link>
            <span className="muted">— creator</span>
          </div>
          {editors.map((e) => (
            <div className="row" key={e.uid} data-testid="credit-row">
              <UserAvatar uid={e.uid} size={20} />
              {e.handle ? (
                <Link to={`/u/${e.handle}`}>@{e.handle}</Link>
              ) : (
                <span>@{e.uid}</span>
              )}
              <span className="muted">— editor</span>
            </div>
          ))}
        </div>
      </div>

      {/* Diverged public forks of this game — lazy list (origin R8). */}
      {!isNative && (
        <ForksPanel
          creationId={creationId}
          forksCount={creation.forksCount ?? 0}
          open={forksOpen}
          onToggle={() => setForksOpen((was) => !was)}
          panelRef={forksPanelRef}
        />
      )}

      {/* Online Arena — host/join a ranked match against another player. Shown
          whenever the active version can be hosted online; the panel itself
          notes when a repackage is still needed for deterministic pairing. */}
      {onlineEligible && (
        <OnlineMatchPanel
          creationId={creationId}
          ready={inviteReady}
          // A Composition V2 version (e.g. Arena Tanks) admits only accepted
          // invitations and starts on bilateral readiness.
          hostEntersAfterAccept={!!activeVersionDoc?.compositionPublicLaunchV2}
        />
      )}

      {/* The game's BOARD. Every game has one now: an Elo ladder for an
          onlineVersus creation, a career-best high-score table for everything
          else — natives included, which used to be excluded outright. The
          panel labels itself from the kind the recompute stamped on the
          snapshot, so this does not need to know which it is. */}
      <CreationLeaderboard
        creationId={gameKey}
        // A creation's kind is derivable here (it mirrors creationDescriptor);
        // a native's is not (apps/* cannot import games/*), but every native
        // always has a snapshot to read it from.
        expectedKind={isNative ? undefined : hasOnlineMode ? 'elo' : 'highscore'}
      />

      {/* Who has actually played this lately — anyone, the creator and editors
          included. Renders nothing until someone has played it. */}
      <RecentPlayers gameKey={gameKey} />

      {/* Everyone who currently likes this game — the faces behind the likes
          count. Renders nothing until it has its first fan. */}
      <Fans gameKey={creationId} />

      {/* Per-GAME comment thread (public read, sign-in to post). */}
      <Comments creationId={creationId} />

      {/* Natives have no builder lifecycle: no version history, no rollback,
          no iterate affordances — the server rejects them anyway. */}
      {!isNative && (
        <div className="panel">
          <h2 className="panel__title">Version history</h2>
          <p className="muted" style={{ marginTop: -6 }}>
            Playing <strong>v{activeVersion}</strong>
            {rolledBack && ' (rolled back — a newer live version exists)'}
            {latestFailed && ` (the latest build, v${creation.latestVersion}, failed — still on this version)`}
            {buildInProgress && ' (a new version is building)'}.
            {canManage ? ' Set any live version active to roll back or forward.' : ''}
          </p>
          {(versionErr || activateErr) && <div className="error-text">{versionErr || activateErr}</div>}
          {versions.length === 0 ? (
            <p className="muted">No published versions yet.</p>
          ) : (
            <div className="table__scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Change</th>
                    <th>Status</th>
                    <th>By</th>
                    <th>Published</th>
                    <th>Active</th>
                    {canManage && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => {
                    const isActive = v.n === activeVersion;
                    const failed = v.buildStatus === 'failed';
                    const elapsed = buildElapsed(v);
                    return (
                      <tr key={v.id}>
                        <td>v{v.n}</td>
                        <td style={{ maxWidth: 320 }}>
                          {/* The build INSTRUCTION for this iteration (provenance). */}
                          <div>{v.prompt ? truncate(v.prompt) : <span className="muted">—</span>}</div>
                          {/* The iterate auto-amend applied to the design for THIS build. */}
                          {v.designAmendment && (
                            <div className="muted" style={{ fontSize: 12 }} data-testid={`design-amendment-${v.n}`}>
                              {truncate(v.designAmendment, 100)}
                            </div>
                          )}
                          {/* The compiler settings-diff on an iteration, when present. */}
                          {v.diffSummary && (
                            <div className="muted" style={{ fontSize: 12 }}>{truncate(v.diffSummary, 100)}</div>
                          )}
                          {/* On a failed build, the reason (gate output tail). */}
                          {failed && v.gates?.notes && (
                            <div className="error-text" style={{ fontSize: 12 }}>{truncate(v.gates.notes, 160)}</div>
                          )}
                        </td>
                        <td>{v.buildStatus}</td>
                        {/* updatedByHandle is '' on legacy rows that predate attribution. */}
                        <td className="muted">{v.updatedByHandle ? `by @${v.updatedByHandle}` : '—'}</td>
                        <td>
                          {v.finishedAt ? new Date(v.finishedAt).toLocaleDateString() : '—'}
                          {elapsed && <div className="muted" style={{ fontSize: 12 }}>{elapsed}</div>}
                        </td>
                        <td>{isActive ? <span className="pill">active</span> : ''}</td>
                        {canManage && (
                          <td>
                            <div className="row">
                              {v.buildStatus === 'live' && !isActive && (
                                <button
                                  className="btn btn--ghost btn--sm"
                                  onClick={() => setActive(v.n)}
                                  disabled={versionBusy !== null || activatingVersion !== null}
                                  data-testid={`set-active-${v.n}`}
                                >
                                  {activatingVersion === v.n ? 'Setting…' : 'Set active'}
                                </button>
                              )}
                              {canDestroy && !isActive && (
                                <button
                                  className="btn btn--danger btn--sm"
                                  onClick={() => removeVersion(v.n)}
                                  disabled={versionBusy !== null || activatingVersion !== null}
                                  data-testid={`delete-version-${v.n}`}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {attributionModal}
    </div>
  );
}

export function GamePage() {
  const { slug = '' } = useParams();
  // Native slugs are reserved. EVERY game — creations + hosted natives + the
  // external native (IceSpace) — shares the ONE doc-backed landing page; the
  // manifest's `link.kind` (hosted vs external) tells the page which standalone
  // URL Start opens.
  const native = nativeGameBySlug(slug);
  return <CreationGamePage slug={slug} native={native} />;
}
